/**
 * Bot Detection Module
 *
 * Provides comprehensive bot detection capabilities to identify and filter out
 * non-human traffic from analytics. Uses multiple detection strategies including
 * user agent analysis, browser feature detection, and behavioral pattern analysis.
 *
 * Split into focused submodules:
 * - `ua-signals` — static user-agent / browser-environment analysis
 * - `interaction-tracker` — behavioral interaction tracking
 * - `session-analysis` — aggregate session pattern analysis
 *
 * @module bot-detection
 */

import {
  analyzeUserAgentForBotSignals,
  type BotDetectionResult,
} from './ua-signals';
import { UserInteractionTracker } from './interaction-tracker';

export {
  analyzeUserAgentForBotSignals,
  type BotDetectionResult,
  type BotType,
  type BotBehaviorFlags,
} from './ua-signals';
export {
  UserInteractionTracker,
  initInteractionTracking,
  type UserInteractionMetrics,
  type HumanBehaviorScore,
} from './interaction-tracker';
export {
  analyzeSessionBehaviorPattern,
  type SessionBehaviorParameters,
} from './session-analysis';

/**
 * How much of a detected bot's user agent may be copied into a log or an
 * analytics parameter. A prefix is enough to recognise the crawler family, and
 * capping it keeps the string well inside GA4's 100-character parameter limit
 * while limiting how much of a fingerprintable string is retained.
 */
export const BOT_USER_AGENT_PREFIX_LENGTH = 50;

export interface ComprehensiveBotDetectionResult extends BotDetectionResult {
  /** Human-likeness of the observed interactions, 0-1. Reported, not gated on. */
  interactionScore: number;
}

/**
 * Entry point for bot detection: static UA/browser signals plus the behavioural
 * flags from the interaction tracker.
 *
 * Read early in a session this is pessimistic by construction — the tracker has
 * had no time to observe anything, so `noInteraction`/`tooFast` will be true for
 * humans too.
 */
export function performComprehensiveBotAnalysis(): ComprehensiveBotDetectionResult {
  const userAgentAnalysis = analyzeUserAgentForBotSignals();
  const interactionTracker = UserInteractionTracker.getInstance();
  const behaviorScore = interactionTracker.calculateHumanBehaviorScore();

  if (!behaviorScore.hasHumanBehavior) {
    userAgentAnalysis.behaviorFlags.noInteraction = true;
  }

  if (behaviorScore.details.timeSpent < 2) {
    userAgentAnalysis.behaviorFlags.tooFast = true;
  }

  // TODO: behaviorFlags and interactionScore are reported but never fed back
  // into isBot — it is still purely the UA-signal confidence. Either wire the
  // behavioural score in or drop the placeholder flags.
  return {
    ...userAgentAnalysis,
    interactionScore: behaviorScore.score,
  };
}
